import {Repository} from "./Repository";
import {AxiosInstance, AxiosRequestConfig, AxiosResponse} from "axios";
import {Idea} from "../models/tickets/Idea";
import {Problem} from "../models/tickets/Problem";
import {QueryParametersInterface} from "./interfaces/QueryParametersInterface";
import {TicketInterface} from "@api/models/interfaces/tickets/TicketInterface";
import {TicketType} from "@share/ticketSystem/enums/TicketTypes";
import { IdeaTicketInterface } from "@api/models/interfaces/tickets/IdeaTicketInterface";
import { ProblemTicketInterface } from "@api/models/interfaces/tickets/ProblemTicketInterface";
import { TicketsQueryWithCountResponseData } from "./interfaces/TicketsDataInterface";

export class TicketRepository implements Repository {
    client: AxiosInstance;
    models = {
        idea: Idea,
        problem: Problem
    };

    constructor(client: AxiosInstance) {
        this.client = client;
    }

    query<T = Idea | Problem>(type: TicketType, query: QueryParametersInterface = {}, config = {}): Promise<T[]> {
        return this.client.post(`/ugmk/tickets/${type}/query`, query, {...config})
            .then((obj) => {
                return (obj.data || []).map((item: TicketInterface) => new this.models[type](item));
            });
    }

    queryEmail(type: TicketType, subject = "", query: QueryParametersInterface = {}) {
        return this.client.post(`/ugmk/tickets/${type}/query/email${subject ? '?subject=' + subject : '' }`, query);
    }

    groupCountQueryEmail(type: TicketType, subject = "", query: QueryParametersInterface = {}) {
        return this.client.post(`/ugmk/tickets/${type}/groupCountQuery/email${subject ? '?subject=' + subject : '' }`, query);
    }

    givenAndAcceptedQueryEmail(type: TicketType, subject = "", statusCode = "", query: QueryParametersInterface = {}) {
        const params: Record<string, any> = {
            ...(subject ? { subject: subject } : null),
            statusCode
        }

        const queryString = Object.keys(params).map(key => key + '=' + params[key]).join('&');

        return this.client.post(`/ugmk/tickets/${type}/givenAndAcceptedQuery/email?${queryString}`, query);
    }

    groupedByWeekQueryEmail(type: TicketType, params: any, query: QueryParametersInterface = {}) {
        const queryString = Object.keys(params).map(key => key + '=' + encodeURIComponent(params[key])).join('&') ;

        return this.client.post(`/ugmk/tickets/${type}/groupedByWeekQuery/email?${queryString}`, query);
    }

    queryWithCount(type: TicketType, query: QueryParametersInterface = {}, config?: AxiosRequestConfig): Promise<TicketsQueryWithCountResponseData> {
        return this.client.post<(ProblemTicketInterface | IdeaTicketInterface)[]>(`/ugmk/tickets/${type}/query?count=true`, query, config)
            .then(({ data, headers }) => {
                return {
                    tickets: (data || []).map((item: TicketInterface) => new this.models[type](item)),
                    count: parseInt(headers['x-appercode-totalitems'] || 0)
                }
            });
    }

    queryRawWithCount<T>(type: TicketType, query: QueryParametersInterface = {}, config?: AxiosRequestConfig) {
        return this.client.post<T[]>(`/ugmk/tickets/${type}/query?count=true`, query, config)
            .then(({ data, headers }) => {
                return {
                    tickets: (data || []).map((item: T) => item),
                    count: parseInt(headers['x-appercode-totalitems'] || 0)
                }
            });
    }

    find(type: TicketType, id: string) {
        return this.client.get<IdeaTicketInterface | ProblemTicketInterface>(`/ugmk/tickets/${type}/${id}`)
            .then((obj) => {
                return new this.models[type](obj.data);
            });
    }

    update(type: string, id: string, changes: any) {
        return this.client.put(`/ugmk/tickets/${type}/${id}`, changes)
    }

    list(type: TicketType, own = false, query: QueryParametersInterface = {}, count = false, config = {}) {
        return this.client.post(`/ugmk/tickets/${type}/list/query?own=${own}&count=${count}`, query, config)
            .then(({ data, headers }) => {
                return {
                    tickets: (data || []).map((item: TicketInterface) => new this.models[type](item)),
                    count: parseInt(headers['x-appercode-totalitems'] || 0)
                }
            });
    }

    async register(type: TicketType, data: any, ownerId: number | null): Promise<AxiosResponse<TicketInterface>> {
        let url = `/ugmk/tickets/${type}/register`;
        if (ownerId) {
            url += `?ownerId=${ownerId}`
        }
        return this.client.post(url, data)
    }

    async create(type: TicketType, requestBody: Record<string, any>): Promise<AxiosResponse<TicketInterface>> {
        return this.client.post(`/ugmk/tickets/${type}`, requestBody);
    }

    async getStatictic(type: TicketType, queryBody: any) {
        const result = await this.client.post(`/ugmk/dashboard/${type}s`, queryBody);
        return result.data;
    }
}
